// Real User Monitoring (Datadog RUM, without Session Replay).
//
// Privacy: like PostHog (web/src/analytics.ts), no message text, names, emails, channel or server names, invite codes,
// or reset tokens ever leave the browser. `defaultPrivacyLevel: "mask"` masks DOM text, and `beforeSend` scrubs view
// URLs (through `route`, stripping query strings like `?token=` and path ids/codes), drops outside referrers, redacts
// API resource paths and third-party resource URLs (such as KLIPY search queries and keys), and clears action target
// text. Do Not Track and Global Privacy Control are respected.
//
// Speed & tests: loaded only after the page has loaded and the browser is idle, so it never touches cold start; off
// entirely when `/api/config` says `analytics: false` (DEV_MODE: local and test servers).
import { datadogRum, type RumEvent } from "@datadog/browser-rum-slim";
import { reactPlugin } from "@datadog/browser-rum-react";
import { route } from "./analytics.ts";
import { getConfig } from "./upload.ts";

export interface RumOptions {
  applicationId: string;
  clientToken: string;
  env: string;
  /** True when the developer explicitly provided a Datadog key via VITE_DD_CLIENT_TOKEN or VITE_DD_APPLICATION_ID. */
  explicitKey?: boolean;
}

const STATIC_API_SEGMENTS = new Set([
  "api",
  "admin",
  "actions",
  "apns",
  "apps",
  "auth",
  "authorize",
  "backups",
  "bloom",
  "bots",
  "channels",
  "complete",
  "config",
  "emoji",
  "events",
  "fcm",
  "files",
  "gif-file",
  "grants",
  "health",
  "history",
  "img",
  "integrations",
  "invites",
  "latency",
  "link-previews",
  "lists",
  "me",
  "messages",
  "metrics",
  "oauth",
  "part",
  "pins",
  "push",
  "run",
  "search",
  "servers",
  "settings",
  "signup",
  "stats",
  "stickers",
  "threads",
  "token",
  "uploads",
  "week",
  "ws",
]);

/** Redacts query strings, hashes, and dynamic ids/codes from a same-origin view URL; drops external URLs. */
export function scrubViewUrl(raw: string, origin = location.origin): string {
  if (!raw) return "";
  try {
    const u = new URL(raw, origin);
    if (u.origin !== origin) return "";
    return `${origin}${route(u.pathname)}`;
  } catch {
    return "";
  }
}

/** Redacts query strings, hashes, and ids/codes from resource URLs; reduces external origins to their origin alone. */
export function scrubResourceUrl(raw: string, origin = location.origin): string {
  if (!raw) return "";
  try {
    const u = new URL(raw, origin);
    if (u.origin !== origin) return u.origin;
    const parts = u.pathname.split("/").filter(Boolean);
    if (parts[0] === "api") {
      const cleaned = parts.map((seg, i) => {
        if (STATIC_API_SEGMENTS.has(seg)) return seg;
        const prev = parts[i - 1];
        if (prev === "invites") return ":code";
        if (prev === "servers") return ":server";
        if (prev === "channels") return ":channel";
        return ":id";
      });
      return `${origin}/${cleaned.join("/")}`;
    }
    return `${origin}${route(u.pathname)}`;
  } catch {
    return "";
  }
}

/** Scrubs URLs, referrers, and action target text from a RUM event before it leaves the browser. */
export function scrubRumEvent(event: RumEvent, origin = location.origin): boolean {
  if (event.view) {
    event.view.url = scrubViewUrl(event.view.url, origin);
    if (event.view.referrer) event.view.referrer = scrubViewUrl(event.view.referrer, origin);
    if (typeof event.view.name === "string" && event.view.name.startsWith("/")) {
      event.view.name = route(event.view.name);
    }
  }
  if (event.type === "resource" && event.resource) {
    event.resource.url = scrubResourceUrl(event.resource.url, origin);
  }
  if (event.type === "action" && event.action?.target) {
    event.action.target.name = "";
  }
  return true;
}

/** Whether Datadog RUM should run given credentials, `/api/config`'s `analytics` flag (or an explicit key override), and browser privacy signals. */
export function shouldEnableRum(
  options: RumOptions,
  analyticsEnabled: boolean | undefined,
  nav: { doNotTrack?: string | null; globalPrivacyControl?: boolean } = navigator,
): boolean {
  return Boolean(
    options.applicationId &&
      options.clientToken &&
      (analyticsEnabled === true || options.explicitKey === true) &&
      nav.doNotTrack !== "1" &&
      nav.globalPrivacyControl !== true,
  );
}

let state: "waiting" | "loading" | "on" | "off" = "waiting";

function initRumNow(options: RumOptions): boolean {
  try {
    datadogRum.init({
      applicationId: options.applicationId,
      clientToken: options.clientToken,
      site: "us5.datadoghq.com",
      service: "bloom-web",
      env: options.env,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask",
      allowedTracingUrls: [(url) => url.startsWith(location.origin + "/api")],
      traceContextInjection: "all",
      beforeSend: (event) => scrubRumEvent(event),
      plugins: [reactPlugin({ router: true })],
    });
    state = "on";
    return true;
  } catch {
    state = "off";
    return false;
  }
}

export async function loadRum(options: RumOptions): Promise<boolean> {
  if (state !== "waiting") return state === "on";
  state = "loading";
  const config = options.explicitKey ? null : await getConfig().catch(() => null);
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  if (!shouldEnableRum(options, config?.analytics, nav)) {
    state = "off";
    return false;
  }
  return initRumNow(options);
}

/** Resets module state for unit tests. */
export function resetRumForTests() {
  state = "waiting";
}

/** Starts Datadog RUM once the page has loaded and the browser is idle (or synchronously when an explicit key override is set). */
export function installRum(options: RumOptions) {
  if (options.explicitKey) {
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
    if (shouldEnableRum(options, undefined, nav)) {
      initRumNow(options);
    } else {
      state = "off";
    }
    return;
  }
  const later = () => {
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    if (idle) idle(() => void loadRum(options), { timeout: 8000 });
    else setTimeout(() => void loadRum(options), 3000);
  };
  if (document.readyState === "complete") setTimeout(later, 1500);
  else addEventListener("load", () => setTimeout(later, 1500), { once: true });
}
